import { useAppContext } from "../../context/AppContext";
import { getUpcomingClasses, bookClass, cancelBooking, updateMyBookingOnline, joinWaitlist, leaveWaitlist } from "../../services/classService";
import { getPassTypes, purchasePass, payUnpaidPass } from "../../services/passService";
import { getUser, updateUser } from "../../services/userService";
import { getPolicy } from "../../services/policyService";
import { format, startOfWeek, endOfWeek, getWeek, isSameDay, eachDayOfInterval } from "date-fns";
import { useEffect, useMemo, useRef, useState } from "react";
import { Link } from "react-router-dom";
import bgImg from "../../assets/images/bg-image-2.png";
import { OnlineSessionsTrans, ScheduleTrans, ToasterTrans, DaysOfTheWeekTrans, translateMonth as translateMonthUtil } from "../../utils/translations";
import { useAuth0 } from '@auth0/auth0-react';
import PriceCard from "../Schedule/PriceCard";
import { getCompactScheduleSummarySegments } from "../../utils/liveLessonsSchedule";
import ActivePassDisplay from "../Schedule/ActivePassDisplay";
import DesktopCalendar from "../Schedule/DesktopCalendar";
import WhatsAppSection from "../Schedule/WhatsAppSection";
import TelegramSection from "../Schedule/TelegramSection";
import { hasActiveStudioPass, hasActiveOnlineClubPass, hasMultiClassPass } from "../../utils/communityPassHelpers";
import PurchaseModal from "../Schedule/PurchaseModal";
import PopupModal from "../Schedule/ClassModal";
import MobileCalendar from "../Schedule/MobileCalendar";
import RegularDaysCard from "../Schedule/RegularDaysCard";
import ScheduleSummaryLine from "../Schedule/ScheduleSummaryLine";
import DynamicLoader from "../utilPages/DynamicLoader";

export default function Offline() {
  const { language, user, setUser, setToaster, token } = useAppContext();
  const details = ScheduleTrans[language];
  const daysTrans = DaysOfTheWeekTrans[language];
  const [classes, setClasses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [passTypes, setPassTypes] = useState([]);
  const [showModal, setShowModal] = useState(false);
  const [selectedClass, setSelectedClass] = useState(null);
  const [bookingStatus, setBookingStatus] = useState(null);
  const [showPurchaseModal, setShowPurchaseModal] = useState(false);
  const [selectedPass, setSelectedPass] = useState(null);
  const [isSubscription, setIsSubscription] = useState(false);
  const [rulesAgreedChecked, setRulesAgreedChecked] = useState(false);
  const [currentPolicy, setCurrentPolicy] = useState(null);
  const { loginWithRedirect } = useAuth0();

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const policyTitle = details.firstPassRulesTitle;
  const policyContent = currentPolicy
    ? (language === "russian" ? currentPolicy.contentRu : currentPolicy.contentEn) || details.firstPassRulesContent
    : details.firstPassRulesContent;

  const policy = user?.policyAgreements;
  const showRulesCheckbox = !!(
    user &&
    (!policy?.agreedAt ||
      (currentPolicy && policy?.version !== currentPolicy.version))
  );

  // Add state for canceling
  const [cancelingBookingId, setCancelingBookingId] = useState(null);

  // Add state for modal cancellation
  const [cancelingFromModal, setCancelingFromModal] = useState(false);

  // Fetch upcoming classes, pass types, and current first-pass rules policy. Pass token when logged in so private classes visible to this user are included.
  useEffect(() => {
    const fetchData = async () => {
      try {
        const [classesRes, passTypesRes, policyRes] = await Promise.all([
          getUpcomingClasses(token),
          getPassTypes(),
          getPolicy(),
        ]);
        setClasses(classesRes);
        setPassTypes(passTypesRes);
        setCurrentPolicy(policyRes);
      } catch (error) {
        setToaster({ message: "Failed to load data", type: "error", show: true });
        setLoading(false);
      }
    };
    fetchData();
  }, [token]);

  // Handle payment success and polling
  const delayedRefetchRef = useRef([]);
  useEffect(() => {
    const searchParams = new URLSearchParams(window.location.search);
    const paymentStatus = searchParams.get('payment');
    const orderId = searchParams.get('orderId');
    const classId = searchParams.get('classId');
    
    // Only proceed if payment was successful and we have an order ID
    if (paymentStatus !== 'success' || !orderId && classes.length && passTypes.length) {
      setLoading(false);
      return;
    };

    setLoading(details.processingPayment);
    let pollCount = 0;
    let pollInterval;
    const maxPolls = 60;
    
    const pollForPass = async () => {
      try {
        const updatedUser = await getUser(token);
        setUser(updatedUser);
        
        // Check if pass was found with this order ID
        const passFound = updatedUser?.passes?.some(pass => 
          pass.allpayOrderId === orderId
        );
        
        if (passFound) {
          setToaster({ 
            message: details.paymentSuccess, 
            type: "success", 
            show: true 
          });
          
          // Scenario 2: Class ID provided - wait for booking
          if (classId) {
            const bookingClassId = (b) => (b.classId && typeof b.classId === "object" ? b.classId._id : b.classId);
            const isClassBooked = updatedUser?.bookings?.some(
              (booking) =>
                booking.status === "confirmed" &&
                bookingClassId(booking) &&
                String(bookingClassId(booking)) === String(classId)
            );
            
            if (isClassBooked) {
              // Class is booked - open modal
              const classToBook = classes.find((c) => c._id === classId);
              if (classToBook) {
                setSelectedClass(classToBook);
                setShowModal(true);
                clearInterval(pollInterval);
                // Scroll to active pass after booking
                const activePassElement = document.querySelector('[data-active-pass]');
                activePassElement?.scrollIntoView({ behavior: 'smooth' });
                setLoading(false);
                // Clear payment params (payment, orderId, classId, passId) from URL after pass found
                window.history.replaceState({}, document.title, window.location.pathname);
              }
            } else if (pollCount < maxPolls) {
              pollCount++;
              return; // Keep polling
            } else {
              // Timeout - webhook may still be finishing; refetch once after a delay
              clearInterval(pollInterval);
              setToaster({ 
                message: "Payment processed. If your booking doesn't appear, refresh the page.", 
                type: "info", 
                show: true 
              });
              window.history.replaceState({}, document.title, window.location.pathname);
              setLoading(false);
              delayedRefetchRef.current = [
                setTimeout(() => getUser(token).then(setUser).catch(() => {}), 3000),
              ];
            }
          } else {
            // Scenario 1: No class ID - just pass purchase; webhook may still be auto-booking regular days
            clearInterval(pollInterval);
            const activePassElement = document.querySelector('[data-active-pass]');
            activePassElement?.scrollIntoView({ behavior: 'smooth' });
            setLoading(false);
            window.history.replaceState({}, document.title, window.location.pathname);
            // Refetch user after a short delay so auto-booked classes (and pass.classesUsed) show up
            const refetch = () => {
              getUser(token).then(setUser).catch(() => {});
            };
            delayedRefetchRef.current = [
              setTimeout(refetch, 2000),
              setTimeout(refetch, 5000),
            ];
          }
        } else if (pollCount < maxPolls) {
          pollCount++;
          // Keep polling
        } else {
          clearInterval(pollInterval);
          setToaster({ 
            message: "Payment processed. Please refresh the page.", 
            type: "info", 
            show: true 
          });
          window.history.replaceState({}, document.title, window.location.pathname);
          setLoading(false);

        }
      } catch (error) {
        console.error("Error polling for pass:", error);
        setLoading(false);
        clearInterval(pollInterval);
      }
    };
    
    // Start polling immediately
    pollForPass();
    pollInterval = setInterval(pollForPass, 1000);
    
    return () => {
      clearInterval(pollInterval);
      delayedRefetchRef.current.forEach(clearTimeout);
      delayedRefetchRef.current = [];
    };
  }, [token, classes, passTypes]);

  const getBookingForClass = (classId) => {
    if (!user?.bookings) return undefined;
    const id = (b) => (b.classId && typeof b.classId === "object" ? b.classId._id : b.classId);
    return user.bookings.find(
      (b) => (id(b) && String(id(b)) === String(classId)) && b.status === "confirmed"
    );
  };

  // Auto-open modal after login
  useEffect(() => {
    if (user && token && classes.length > 0 && passTypes.length > 0) {
      const pendingRedirectTo = window.localStorage.getItem("pendingRedirectTo");
      const pendingClassId = window.localStorage.getItem("pendingClassId");
      const openPurchaseModal = window.localStorage.getItem("openPurchaseModal");
      const openBookingModal = window.localStorage.getItem("openBookingModal");
      const scrollPosition = sessionStorage.getItem("scrollPosition");

      if (pendingRedirectTo !== "/schedule") return;

      // Had a class selected: restore class and open booking or purchase modal
      if (pendingClassId) {
        const classToBook = classes.find((c) => c._id === pendingClassId);
        if (classToBook) {
          const isAlreadyBooked = getBookingForClass(pendingClassId);
          if (isAlreadyBooked) {
            setShowPurchaseModal(false);
            setIsSubscription(false);
            handleSetShowModal(false);
            setToaster({
              message: details.alreadyBooked || "You are already booked for this class",
              type: "info",
              show: true,
            });
          } else {
            setSelectedClass(classToBook);
            if (openBookingModal === "true") {
              setShowModal(true);
              setShowPurchaseModal(false);
              setIsSubscription(false);
            } else if (openPurchaseModal === "true") {
              setShowPurchaseModal(true);
              setIsSubscription(false);
              setShowModal(true);
              const availablePasses = getFilteredPassTypesByLocation();
              setSelectedPass(availablePasses[0] || passTypes[0]);
            }
            if (scrollPosition) {
              setTimeout(() => {
                window.scrollTo(0, parseInt(scrollPosition));
                sessionStorage.removeItem("scrollPosition");
              }, 100);
            }
          }
        }
      } else if (openPurchaseModal === "true") {
        // No class (e.g. opened from prices): just reopen purchase modal
        setShowPurchaseModal(true);
        setIsSubscription(false);
        setSelectedClass(null);
        const availablePasses = getFilteredPassTypesByLocation();
        setSelectedPass(availablePasses[0] || passTypes[0]);
        if (scrollPosition) {
          setTimeout(() => {
            window.scrollTo(0, parseInt(scrollPosition));
            sessionStorage.removeItem("scrollPosition");
          }, 100);
        }
      }

      // Clean up
      window.localStorage.removeItem("pendingRedirectTo");
      window.localStorage.removeItem("pendingClassId");
      window.localStorage.removeItem("openPurchaseModal");
      window.localStorage.removeItem("openBookingModal");
    }
  }, [user, token, classes, passTypes]);

  // Open class modal from URL when visiting a shared link (e.g. /schedule?classId=xxx)
  useEffect(() => {
    if (classes.length === 0) return;
    const searchParams = new URLSearchParams(window.location.search);
    const urlClassId = searchParams.get("classId");
    const paymentStatus = searchParams.get("payment");
    const pendingRedirect = window.localStorage.getItem("pendingRedirectTo") === "/schedule" && window.localStorage.getItem("pendingClassId");
    if (!urlClassId || paymentStatus === "success" || pendingRedirect) return;
    const classToShow = classes.find((c) => c._id === urlClassId);
    if (classToShow) {
      setSelectedClass(classToShow);
      setShowModal(true);
      setBookingStatus(null);
    }
  }, [classes]);

  // Keep URL in sync: remove classId when modal is closed
  useEffect(() => {
    if (!showModal && typeof window !== "undefined") {
      const url = new URL(window.location.href);
      if (url.searchParams.has("classId")) {
        url.searchParams.delete("classId");
        window.history.replaceState({}, document.title, url.pathname + (url.search || ""));
      }
    }
  }, [showModal]);

  const handleBookClick = (classObj) => {
    setSelectedClass(classObj);
    setShowModal(true);
    setBookingStatus(null);
    // Update URL so the link can be shared
    const url = new URL(window.location.href);
    url.searchParams.set("classId", classObj._id);
    window.history.replaceState({}, document.title, url.pathname + url.search);
  };

  const handleSetShowModal = (value) => {
    setShowModal(value);
    if (value === false) {
      const url = new URL(window.location.href);
      url.searchParams.delete("classId");
      window.history.replaceState({}, document.title, url.pathname + (url.search || ""));
    }
  };

  // Add this helper function before the return statement
  const isClassDateWithinAnyPass = (classDate) => {
    if (!user?.passes) return false;
    
    const now = new Date();
    const classDateTime = new Date(classDate);
    
    return user.passes.some(pass =>
      pass.status === "active" &&
      new Date(pass.expiresAt) > now &&
      classDateTime <= new Date(pass.expiresAt)
    );
  };

  // Mirror server classService pass rules (isPassValidForLocation + getPassForClassDate).
  // classType/allowedClassTypes are the eligibility data for normal in-person booking;
  // Class.allowJoinOnline is a property of the class (not the pass) — any active pass may be
  // used to join online when the class itself allows it. classType now always arrives populated
  // as { _id, name, displayName } from the backend, so no client-side derivation fallback is needed.
  const classTypeIdOf = (v) => (v ? String(v?._id || v) : null);

  /** Same as server: online = join-online on a non-online class (POST body online: true) */
  const isPassValidForLocation = (pass, classObj, online = false) => {
    const classTypeId = classTypeIdOf(classObj?.classType);
    const allowedClassTypeIds = (pass.passTypeId?.allowedClassTypes || []).map(classTypeIdOf);

    if (classObj?.isIndividual === true) {
      return classTypeId != null && allowedClassTypeIds.includes(classTypeId);
    }

    const isOnlineClass = classObj?.type === "online";
    if ((isOnlineClass || classObj?.allowJoinOnline === true) && (online || isOnlineClass)) {
      return true;
    }

    return classTypeId != null && allowedClassTypeIds.includes(classTypeId);
  };

  /**
   * Same filter + sort as server getPassForClassDate; optional online for join-online on offline classes.
   * Returns first pass that covers class date (expires after class start), matching server behavior.
   */
  const getActivePassForUserAndLocation = (classObj, online = false) => {
    if (!user?.passes || !classObj) return null;
    const now = new Date();
    const classDateTime = new Date(classObj.start);

    const validPasses = user.passes.filter(
      (pass) =>
        pass.status === "active" &&
        new Date(pass.expiresAt) > now &&
        (!pass.classesIncluded || pass.classesUsed < pass.classesIncluded) &&
        isPassValidForLocation(pass, classObj, online)
    );

    if (!validPasses.length) return null;

    validPasses.sort((a, b) => {
      const aIsTrial = !!a.passTypeId?.trial;
      const bIsTrial = !!b.passTypeId?.trial;
      if (aIsTrial && !bIsTrial) return -1;
      if (!aIsTrial && bIsTrial) return 1;

      // Prefer spending a pass restricted to fewer class types before a broad multi-class
      // pass — a pass scoped to this specific class type is "more specific" to this booking,
      // regardless of what it's restricted to. Mirrors server classService.js.
      const aScope = a.passTypeId?.allowedClassTypes?.length ?? Infinity;
      const bScope = b.passTypeId?.allowedClassTypes?.length ?? Infinity;
      if (aScope !== bScope) return aScope - bScope;

      return new Date(a.expiresAt) - new Date(b.expiresAt);
    });

    const passForClass = validPasses.find(
      (pass) => classDateTime <= new Date(pass.expiresAt)
    );
    return passForClass ?? null;
  };

  // Update handleConfirmBooking to use location-aware pass check. Pass online=true to book as "join online" (unlimited, does not affect studio bookedCount).
  const handleConfirmBooking = async (online = false) => {
    try {
      if (!token) {
        // No token - open purchase modal
        setShowPurchaseModal(true);
        setIsSubscription(false);
        setSelectedPass(getFilteredPassTypesByLocation()[0] || passTypes[0]);
        setShowModal(true);
        return;
      }

      if (!user) return;

      // Check if user has valid pass for this class location (online must match booking payload)
      const activePass = getActivePassForUserAndLocation(selectedClass, online);
      if (!activePass) {
        setShowPurchaseModal(true);
        setIsSubscription(false);
        setSelectedPass(getFilteredPassTypesByLocation()[0] || passTypes[0]);
        setShowModal(true);
        return;
      }

      // Check if class date is within pass expiration
      if (!isClassDateWithinAnyPass(selectedClass.start)) {
        setShowPurchaseModal(true);
        setIsSubscription(false);
        setSelectedPass(getFilteredPassTypesByLocation()[0] || passTypes[0]);
        setShowModal(true);
        return;
      }

      // Check if pass is exhausted
      if (isPassExhausted(activePass)) {
        setShowPurchaseModal(true);
        setIsSubscription(false);
        setSelectedPass(getFilteredPassTypesByLocation()[0] || passTypes[0]);
        setShowModal(true);
        return;
      }

      setBookingStatus("loading");
      await bookClass(selectedClass._id, token, { online: !!online });
      setBookingStatus("success");
      handleSetShowModal(false);
      setToaster({ message: details.bookingSuccess, type: "success", show: true });
      const [updatedUser, freshClasses] = await Promise.all([
        getUser(token),
        getUpcomingClasses(token),
      ]);
      setUser(updatedUser);
      setClasses(freshClasses);
    } catch (error) {
      setBookingStatus("error");
      const isClassFull = error.message === "Class is full";
      setToaster({
        message: isClassFull ? details.studioFullCannotSwitch : details.bookingError,
        type: "error",
        show: true,
      });
      // Refresh classes so the UI reflects current capacity
      try {
        const freshClasses = await getUpcomingClasses(token);
        setClasses(freshClasses);
        if (isClassFull && selectedClass) {
          const updated = freshClasses.find((c) => c._id === selectedClass._id);
          if (updated) setSelectedClass(updated);
        }
      } catch (_) {}
    }
  };

  // Add cancel booking handler
  const handleCancelBooking = async (classId) => {
    if (!user || !token) return;
    try {
      await cancelBooking(classId, token);
      setCancelingBookingId(null); // Reset accordion cancel state
      setCancelingFromModal(false);
      setToaster({ message: details.bookingCancelled, type: "warning", show: true });
      handleSetShowModal(false);
      const [updatedUser, freshClasses] = await Promise.all([
        getUser(token),
        getUpcomingClasses(token),
      ]);
      setUser(updatedUser);
      setClasses(freshClasses);
    } catch (error) {
      setToaster({ message: details.bookingCancelledError, type: "error", show: true });
    }
  };

  const [togglingOnlineClassId, setTogglingOnlineClassId] = useState(null);
  const handleToggleBookingOnline = async (classId, online) => {
    if (!user || !token) return;
    setTogglingOnlineClassId(classId);
    try {
      await updateMyBookingOnline(classId, token, online);
      const [updatedUser, freshClasses] = await Promise.all([
        getUser(token),
        getUpcomingClasses(token),
      ]);
      setUser(updatedUser);
      setClasses(freshClasses);
    } catch (err) {
      const isStudioFull = err.message === "Class is full; cannot switch to in-studio";
      setToaster({
        message: isStudioFull ? details.studioFullCannotSwitch : (err.message || details.bookingError),
        type: "error",
        show: true,
      });
    } finally {
      setTogglingOnlineClassId(null);
    }
  };

  const handleJoinWaitlist = async (classId) => {
    if (!user || !token) return;
    try {
      await joinWaitlist(classId, token);
      setToaster({ message: details.waitlistJoined, type: "success", show: true });
      const freshClasses = await getUpcomingClasses(token);
      setClasses(freshClasses);
      if (selectedClass) {
        const updated = freshClasses.find((c) => c._id === selectedClass._id);
        if (updated) setSelectedClass(updated);
      }
    } catch (error) {
      setToaster({ message: error.message || "Failed to join waiting list", type: "error", show: true });
    }
  };

  const handleLeaveWaitlist = async (classId) => {
    if (!user || !token) return;
    try {
      await leaveWaitlist(classId, token);
      setToaster({ message: details.waitlistLeft, type: "warning", show: true });
      const freshClasses = await getUpcomingClasses(token);
      setClasses(freshClasses);
      if (selectedClass) {
        const updated = freshClasses.find((c) => c._id === selectedClass._id);
        if (updated) setSelectedClass(updated);
      }
    } catch (error) {
      setToaster({ message: error.message || "Failed to leave waiting list", type: "error", show: true });
    }
  };

  // Group classes by week
  const weeks = {};
  classes.forEach((classObj) => {
    const weekNum = getWeek(new Date(classObj.start));
    if (!weeks[weekNum]) weeks[weekNum] = [];
    weeks[weekNum].push(classObj);
  });

  // Helper: get classes for a specific date
  const getClassesForDate = (date) => {
    return classes.filter((classObj) =>
      isSameDay(new Date(classObj.start), date)
    );
  };

  // Build calendar weeks - start from this week
  const today = new Date();
  const weekStart = startOfWeek(today);

  // Show 4 weeks starting from this week
  const calendarStart = weekStart;
  const calendarEnd = new Date(weekStart);
  calendarEnd.setDate(calendarEnd.getDate() + 28); // 4 weeks

  const calendarDays = eachDayOfInterval({ start: calendarStart, end: calendarEnd });
  const calendarWeeks = [];
  for (let i = 0; i < calendarDays.length; i += 7) {
    calendarWeeks.push(calendarDays.slice(i, i + 7));
  }

  // Helper: translate day names
  const translateDayName = (dayName) => {
    return daysTrans[dayName.toLowerCase()] || dayName;
  };

  const translateMonth = (monthAbbr) => translateMonthUtil(monthAbbr, daysTrans);

  // Add this helper function to translate location names
  const translateLocationName = (locationName) => {
    const locationMap = {
      "Park Leumi": details.parkLeumi,
      "Studio": details.studio,
      "Ramat Gan Rooftop": details.ramatGanRooftop,
    };
    return locationMap[locationName] || locationName;
  };

  // Add helper to get active pass (paid and unpaid both count on Schedule)
  const getActivePassForUser = () => {
    if (!user?.passes) return null;
    const now = new Date();
    return user.passes?.find(
      (pass) =>
        pass.status === "active" &&
        new Date(pass.expiresAt) > now &&
        (!pass.classesIncluded || pass.classesUsed < pass.classesIncluded)
    );
  };

  const isPassExhausted = (pass) => {
    return pass.classesIncluded && pass.classesUsed >= pass.classesIncluded;
  };

  // Accepts either a pass type object (preferred, reads name from the DB) or a raw name string
  const translatePassName = (passTypeOrName) => {
    const pt = passTypeOrName && typeof passTypeOrName === "object" ? passTypeOrName : null;
    const name = pt ? pt.name : passTypeOrName;
    if (pt) {
      const dbName = language === "russian" ? (pt.displayNameRu || pt.displayName) : pt.displayName;
      if (dbName) return dbName;
    }
    const passMap = {
      trial: details.trial,
      park: details.park,
      single: details.dropInClass,
      basic: details.basicPass,
      standard: details.standartPass,
      limitless: details.limitless,
      "individual-1": details["individual-1"],
      "individual-4": details["individual-4"],
      "individual-8": details["individual-8"],
      "individual-12": details["individual-12"],
    };
    return passMap[name] ?? details[name] ?? name;
  };

  // Handle pass purchase
  const handlePurchasePass = async () => {
    if (!selectedPass) return;
    
    // If user not logged in, redirect to login
    if (!user || !token) {
      sessionStorage.setItem("scrollPosition", window.scrollY.toString());
      window.localStorage.setItem("pendingRedirectTo", "/schedule");
      window.localStorage.setItem("redirectTo", "/schedule");
      window.localStorage.setItem("pendingClassId", selectedClass?._id || "");
      window.localStorage.setItem("openPurchaseModal", "true");
      loginWithRedirect();
      return;
    }

    if (showRulesCheckbox && !rulesAgreedChecked) {
      setToaster({ message: details.firstPassRulesAgreementRequired, type: "error", show: true });
      return;
    }

    try {
      if (showRulesCheckbox && rulesAgreedChecked) {
        await updateUser(token, {
          policyAgreements: {
            ...user?.policyAgreements,
            agreedAt: new Date().toISOString(),
            ...(currentPolicy?.version != null && {
              version: currentPolicy.version,
            }),
          },
        });
        const updatedUser = await getUser(token);
        setUser(updatedUser);
      }

      const response = await purchasePass(
        selectedPass._id, 
        token, 
        isSubscription,
        selectedClass?._id
      );

      if (response?.paymentUrl) {
        window.location.href = response.paymentUrl;
        return;
      };
      
      setToaster({ message: "Failed to purchase pass", type: "error", show: true });
      setShowPurchaseModal(false);
      setIsSubscription(false);
      setSelectedPass(null);
      setIsSubscription(false);
      
      const updatedUser = await getUser(token);
      setUser(updatedUser);
    } catch (error) {
      setToaster({ message: "Failed to purchase pass", type: "error", show: true });
    }
  };

  const getFilteredPassTypesByLocation = () => {
    const hasBoughtPass = (user?.passes?.length || 0) > 0;

    let filtered = passTypes;

    // Hide trial pass types once the user has bought any pass
    if (hasBoughtPass) {
      filtered = filtered.filter(p => !p.trial);
    }

    // Only filter by location if a class is selected (coming from class booking)
    if (selectedClass) {
      const classTypeId = classTypeIdOf(selectedClass.classType);
      filtered = filtered.filter(p => (p.allowedClassTypes || []).map(classTypeIdOf).includes(classTypeId));
    }

    // Order the same way PriceCard does: group by section label, ordering groups
    // and passes within them by price (lowest first), so the purchase modal list
    // matches the schedule's price list order.
    const sortedByPrice = [...filtered].sort((a, b) => a.basePrice - b.basePrice);
    const sectionLabelOf = (passType) =>
      (language === "russian" ? passType.sectionLabelRu || passType.sectionLabel : passType.sectionLabel) || "";
    const groups = [];
    const groupIndexByLabel = new Map();
    sortedByPrice.forEach((passType) => {
      const label = sectionLabelOf(passType);
      if (!groupIndexByLabel.has(label)) {
        groupIndexByLabel.set(label, groups.length);
        groups.push([]);
      }
      groups[groupIndexByLabel.get(label)].push(passType);
    });
    return groups.flat();
  };

  const getActiveDiscount = (passType) => {
    const now = new Date();
    return passType.discounts?.find(
      (d) => d.active && new Date(d.validFrom) <= now && new Date(d.validTo) >= now
    );
  };

  /**
   * Active subscription that restricts multiclass purchase (one-time only).
   * Online Club subscription is excluded — users can still buy studio multiclass passes.
   */
  const hasActiveSubscription = () => {
    if (!user?.passes) return false;
    const now = new Date();
    return user.passes.some((pass) => {
      if (
        !pass.isSubscription ||
        !pass.subscriptionActive ||
        new Date(pass.expiresAt) <= now
      )
        return false;
      const name = String(pass.passTypeId?.name || pass.name || "");
      if (name.includes("onlineClub")) return false;
      return true;
    });
  };

  // if (loading || isPolling) {
  //   return (
  //     <div className="flex justify-center items-center min-h-screen">
  //       <p className="text-base">{details.loading}</p>
  //     </div>
  //   );
  // }

  const scheduleSummarySegments = useMemo(
    () => getCompactScheduleSummarySegments(classes, details, daysTrans),
    [classes, details, daysTrans]
  );

  return (
    <>
      <DynamicLoader loading={loading} />
      <main className="flex min-h-screen flex-col">
        <div className="h-[55vh] w-full overflow-hidden">
          <img src={bgImg} alt="Banner" className="w-full h-full object-cover" />
        </div>

        <div className="w-10/12 mx-auto mb-6">
          <h1>{OnlineSessionsTrans[language].header}</h1>
        </div>

        <ActivePassDisplay
          user={user}
          language={language}
          isPassExhausted={isPassExhausted}
          translatePassName={translatePassName}
          translateMonth={translateMonth}
          dataAttribute="data-active-pass"
          onPayUnpaid={token ? async (passId) => {
            try {
              const res = await payUnpaidPass(passId, token);
              if (res?.paymentUrl) window.location.href = res.paymentUrl;
            } catch (e) {
              setToaster({ message: e?.message || details.bookingCancelledError, type: "error", show: true });
            }
          } : undefined}
        />

        {/* Mobile: Calendar Grid */}
        <MobileCalendar
          classes={classes}
          language={language}
          getBookingForClass={getBookingForClass}
          handleBookClick={handleBookClick}
          translateMonth={translateMonth}
          translateLocationName={translateLocationName}
          handleCancelBooking={handleCancelBooking}
          setCancelingBookingId={setCancelingBookingId}
          cancelingBookingId={cancelingBookingId}
          selectedClass={selectedClass}
          setSelectedClass={setSelectedClass}
          showClassModal={showModal}
        />

        {/* Desktop: Calendar Grid */}
        <DesktopCalendar
          classes={classes}
          language={language}
          today={today}
          getBookingForClass={getBookingForClass}
          handleBookClick={handleBookClick}
          getClassesForDate={getClassesForDate}
          translateMonth={translateMonth}
          translateLocationName={translateLocationName}
          isAdmin={user?.role === "admin"}
        />

        <ScheduleSummaryLine
          segments={scheduleSummarySegments}
          details={details}
          language={language}
        />

        {user && token && hasMultiClassPass(user) && (
          <div className="w-full flex justify-center mt-7">
            <RegularDaysCard
              user={user}
              token={token}
              language={language}
              setUser={setUser}
              setToaster={setToaster}
              className="max-w-2xl mx-4"
              centerTitle
            />
          </div>
        )}

        <div className="w-full mt-3 max-w-3xl mx-auto px-4 sm:px-5 md:px-3 text-center mb-0">
          <Link
            to="/onlineClub"
            className="inline-block text-[11px] sm:text-xs md:text-sm font-medium text-brown hover:underline py-0 leading-tight"
          >
            {details.learnAboutOnlineClub}
          </Link>
        </div>

        <div className="w-11/12 mt-4 mb-6 mx-auto">
          <PriceCard
            passTypes={passTypes}
            language={language}
            selectedPass={selectedPass}
            setSelectedPass={setSelectedPass}
            setShowPurchaseModal={setShowPurchaseModal}
            getActiveDiscount={getActiveDiscount}
            translatePassName={translatePassName}
            setSelectedClass={setSelectedClass}
            setIsSubscription={setIsSubscription}
          />
        </div>

        {/* Booking Modal */}
        {showModal && selectedClass && (
          <PopupModal
            showModal={showModal}
            selectedClass={selectedClass}
            user={user}
            token={token}
            language={language}
            details={details}
            isAdmin={user?.role === "admin"}
            getBookingForClass={getBookingForClass}
            isClassDateWithinAnyPass={isClassDateWithinAnyPass}
            getActivePassForUserAndLocation={getActivePassForUserAndLocation}
            isPassExhausted={isPassExhausted}
            translateDayName={translateDayName}
            translateMonth={translateMonth}
            translateLocationName={translateLocationName}
            handleConfirmBooking={handleConfirmBooking}
            handleCancelBooking={handleCancelBooking}
            handleToggleBookingOnline={handleToggleBookingOnline}
            togglingOnlineClassId={togglingOnlineClassId}
            cancelingFromModal={cancelingFromModal}
            setCancelingFromModal={setCancelingFromModal}
            bookingStatus={bookingStatus}
            setShowModal={handleSetShowModal}
            setSelectedClass={setSelectedClass}
            setShowPurchaseModal={setShowPurchaseModal}
            getActivePassForUser={getActivePassForUser}
            loginWithRedirect={loginWithRedirect}
            handleJoinWaitlist={handleJoinWaitlist}
            handleLeaveWaitlist={handleLeaveWaitlist}
            setToaster={setToaster}
          />
        )}

        {showPurchaseModal && (
          <PurchaseModal
            showPurchaseModal={showPurchaseModal}
            setShowPurchaseModal={(open) => {
              setShowPurchaseModal(open);
              if (!open) setRulesAgreedChecked(false);
            }}
            selectedClass={selectedClass}
            setSelectedClass={setSelectedClass}
            language={language}
            details={details}
            getFilteredPassTypesByLocation={getFilteredPassTypesByLocation}
            selectedPass={selectedPass}
            setSelectedPass={setSelectedPass}
            isSubscription={isSubscription}
            setIsSubscription={setIsSubscription}
            hasActiveSubscription={hasActiveSubscription}
            getActiveDiscount={getActiveDiscount}
            handlePurchasePass={handlePurchasePass}
            translatePassName={translatePassName}
            showRulesCheckbox={showRulesCheckbox}
            rulesAgreedChecked={rulesAgreedChecked}
            setRulesAgreedChecked={setRulesAgreedChecked}
            policyTitle={policyTitle}
            policyContent={policyContent}
            isLoggedIn={!!(user && token)}
          />
        )}

        {(() => {
          const hasStudio = hasActiveStudioPass(user);
          const hasOnlineClub = hasActiveOnlineClubPass(user);
          const sideBySideClass =
            "flex-1 min-w-0 max-w-md w-full flex justify-center";
          const bothCommunities = (
            <div className="flex flex-col md:flex-row flex-wrap justify-center items-stretch gap-4 md:gap-6 md:w-1/2 max-w-4xl mx-auto mb-10">
              <WhatsAppSection wrapClassName={sideBySideClass} />
              <TelegramSection isOnlineClubContext wrapClassName={sideBySideClass} />
            </div>
          );
          // Guests: hasActiveStudioPass(null) is true by mistake — show both channels explicitly
          if (!(user && token)) return bothCommunities;
          if (hasStudio && hasOnlineClub) return bothCommunities;
          if (hasOnlineClub) return <TelegramSection />;
          if (hasStudio) return <WhatsAppSection />;
          return <div className="mb-10"><TelegramSection /></div>;
        })()}
      </main>
    </>
  );
}
